class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.collectionIndexPromise = null;
    this.productIndexPromise = null;
    this.requestSequence = 0;
    this.isComposing = false;
    this.skipCompositionEnterKeyup = false;
    this.marketSearch = window.TimeseekMarketSearch ? new window.TimeseekMarketSearch.MarketSearch(this) : null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.input.addEventListener('compositionstart', () => {
      this.isComposing = true;
      this.requestSequence += 1;
      this.marketSearch?.reset();
    });
    this.input.addEventListener('compositionend', () => {
      this.isComposing = false;
      this.skipCompositionEnterKeyup = true;
      this.onChange();
    });
    this.addEventListener('pointerdown', () => {
      this.skipCompositionEnterKeyup = false;
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    if (this.isComposing) return;
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.requestSequence += 1;
      this.marketSearch?.reset();
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (this.isComposing || this.skipCompositionEnterKeyup || !this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.requestSequence += 1;
      this.marketSearch?.reset();
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    if (this.isComposing) return;
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.marketSearch?.render();
      if (this.getAttribute('results') === 'true') this.open();
      else this.getSearchResults(currentSearchTerm);
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (this.isComposing || event.isComposing || event.keyCode === 229) return;
    if (this.skipCompositionEnterKeyup && event.code === 'Enter') {
      this.skipCompositionEnterKeyup = false;
      return;
    }
    this.skipCompositionEnterKeyup = false;
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (this.isComposing || event.isComposing || event.keyCode === 229) return;
    this.skipCompositionEnterKeyup = false;
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (previousTerm && currentButtonText.split(previousTerm).length > 2) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const sequence = ++this.requestSequence;
    const isCurrent = () => sequence === this.requestSequence && searchTerm === this.getQuery() && !this.isComposing;
    const queryKey = searchTerm.toLowerCase();
    this.setLiveRegionLoadingState();
    this.marketSearch?.update(searchTerm);

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(`Predictive search request failed with ${response.status}`);
          error.status = response.status;
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (!isCurrent()) return;
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (!isCurrent()) return;
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }

        this.getFallbackSearchResults(searchTerm)
          .then((fallbackMarkup) => {
            if (!isCurrent()) return;
            if (!fallbackMarkup) {
              if (this.marketSearch?.render({ preserveSelection: true })) return;
              this.close();
              return;
            }

            this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
              predictiveSearchInstance.cachedResults[queryKey] = fallbackMarkup;
            });
            this.renderSearchResults(fallbackMarkup);
          })
          .catch((fallbackError) => {
            if (!isCurrent()) return;
            if (this.marketSearch?.render({ preserveSelection: true })) return;
            this.close();
            throw fallbackError;
          });
      });
  }

  getFallbackSearchResults(searchTerm) {
    const fallbackUrl = `/search?q=${encodeURIComponent(searchTerm)}&type=product,collection&options[prefix]=last`;
    const searchDocumentPromise = fetch(fallbackUrl, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) return null;
        return response.text();
      })
      .then((text) => (text ? new DOMParser().parseFromString(text, 'text/html') : null))
      .catch(() => null);

    return Promise.all([
      searchDocumentPromise,
      this.getCollectionIndex().catch(() => []),
      this.getProductIndex().catch(() => []),
    ]).then(([parsedDocument, collectionIndex, productIndex]) =>
      this.buildFallbackResultsMarkup(searchTerm, parsedDocument, collectionIndex, productIndex)
    );
  }

  buildFallbackResultsMarkup(searchTerm, documentRoot, collectionIndex, productIndex) {
    const searchResultsRoot = documentRoot?.querySelector('.template-search__results, #product-grid') || documentRoot;
    const normalizedTerm = this.normalizeForMatch(searchTerm);
    const collectionCandidatesFromIndex = this.extractCollectionCandidatesFromIndex(collectionIndex, normalizedTerm);
    const productCandidatesFromIndex = this.extractProductCandidatesFromIndex(productIndex, normalizedTerm);
    const collectionCandidates =
      collectionCandidatesFromIndex.length > 0
        ? collectionCandidatesFromIndex
        : this.extractCollectionCandidates(searchResultsRoot, normalizedTerm);
    const productCandidates =
      productCandidatesFromIndex.length > 0
        ? productCandidatesFromIndex
        : this.extractProductCandidates(searchResultsRoot, normalizedTerm);

    const groupedCollections = {
      brand: this.rankCollectionCandidates(collectionCandidates.filter((item) => item.handle.startsWith('brand-'))).slice(
        0,
        4
      ),
      model: this.rankCollectionCandidates(collectionCandidates.filter((item) => item.handle.startsWith('model-'))).slice(
        0,
        4
      ),
      ref: this.rankCollectionCandidates(collectionCandidates.filter((item) => item.handle.startsWith('ref-'))).slice(0, 4),
    };
    const products = this.rankProductCandidates(productCandidates).slice(0, 4);
    const totalCount =
      groupedCollections.brand.length + groupedCollections.model.length + groupedCollections.ref.length + products.length;

    if (totalCount === 0) return '';

    const renderCollectionGroup = (groupId, label, items) => {
      if (!items.length) return '';
      return `
        <h2 id="${groupId}" class="predictive-search__heading text-body caption-with-letter-spacing">${label}</h2>
        <ul class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="${groupId}">
          ${items
            .map(
              (item, index) => `
            <li id="predictive-search-option-${groupId}-${index}" class="predictive-search__list-item" role="option" aria-selected="false">
              <a href="${item.url}" class="predictive-search__item link link--text" tabindex="-1">
                <div class="predictive-search__item-content predictive-search__item-content--centered">
                  <p class="predictive-search__item-heading h5">${this.escapeHtml(item.title)}</p>
                </div>
              </a>
            </li>`
            )
            .join('')}
        </ul>`;
    };

    const renderProducts = () => {
      if (!products.length) return '';
      return `
        <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">商品候補</h2>
        <ul class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
          ${products
            .map(
              (item, index) => `
            <li id="predictive-search-option-product-fallback-${index}" class="predictive-search__list-item" role="option" aria-selected="false">
              <a href="${item.url}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
                ${
                  item.imageSrc
                    ? `<img class="predictive-search__image" src="${this.escapeHtml(item.imageSrc)}" alt="${this.escapeHtml(
                        item.imageAlt || item.title
                      )}" width="50" height="50" loading="lazy">`
                    : ''
                }
                <div class="predictive-search__item-content${item.imageSrc ? '' : ' predictive-search__item-content--centered'}">
                  <p class="predictive-search__item-heading h5">${this.escapeHtml(item.title)}</p>
                </div>
              </a>
            </li>`
            )
            .join('')}
        </ul>`;
    };

    return `
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
          <div class="predictive-search__result-group">
            ${renderCollectionGroup('predictive-search-brands-fallback', 'ブランド候補', groupedCollections.brand)}
            ${renderCollectionGroup('predictive-search-models-fallback', 'モデル候補', groupedCollections.model)}
            ${renderCollectionGroup('predictive-search-refs-fallback', '型番候補', groupedCollections.ref)}
          </div>
          <div class="predictive-search__result-group">
            ${renderProducts()}
          </div>
        </div>
        <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
          <a class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false" href="/search?q=${encodeURIComponent(
            searchTerm
          )}&options%5Bprefix%5D=last">
            <span>「${this.escapeHtml(searchTerm)}」の検索結果を見る</span>
            <span aria-hidden="true">→</span>
          </a>
        </div>
        <span class="hidden" data-predictive-search-live-region-count-value>${totalCount}件の候補</span>
      </div>`;
  }

  getCollectionIndex() {
    if (!this.collectionIndexPromise) {
      this.collectionIndexPromise = this.fetchPaginatedJson('/collections.json', 'collections', 250, 4).then((items) =>
        items
          .filter((item) => item?.handle && item?.title)
          .map((item) => ({
            handle: item.handle,
            title: item.title,
            url: `/collections/${item.handle}`,
          }))
      );
    }
    return this.collectionIndexPromise;
  }

  getProductIndex() {
    if (!this.productIndexPromise) {
      this.productIndexPromise = this.fetchPaginatedJson('/products.json', 'products', 250, 4).then((items) =>
        items
          .filter((item) => item?.handle && item?.title)
          .map((item) => ({
            handle: item.handle,
            title: item.title,
            url: `/products/${item.handle}`,
            imageSrc: this.normalizeImageUrl(item?.images?.[0]?.src || ''),
            imageAlt: item.title,
          }))
      );
    }
    return this.productIndexPromise;
  }

  fetchPaginatedJson(pathname, key, limit = 250, maxPages = 4) {
    const allItems = [];
    const fetchPage = (page) => {
      const pageUrl = `${pathname}?limit=${limit}&page=${page}`;
      return fetch(pageUrl, { signal: this.abortController.signal })
        .then((response) => {
          if (!response.ok) return [];
          return response.json();
        })
        .then((data) => (Array.isArray(data?.[key]) ? data[key] : []));
    };

    const iterate = (page) =>
      fetchPage(page).then((items) => {
        allItems.push(...items);
        if (items.length < limit || page >= maxPages) return allItems;
        return iterate(page + 1);
      });

    return iterate(1);
  }

  extractCollectionCandidatesFromIndex(collectionIndex, normalizedTerm) {
    if (!Array.isArray(collectionIndex) || !collectionIndex.length) return [];

    const directMatches = collectionIndex
      .filter((item) => item.handle?.startsWith('brand-') || item.handle?.startsWith('model-') || item.handle?.startsWith('ref-'))
      .map((item) => ({
        title: item.title,
        handle: item.handle,
        url: item.url,
        score: this.getCollectionRelevanceScore(item.handle, item.title, normalizedTerm),
      }))
      .filter((item) => item.score > 1);

    const candidatesByHandle = new Map(directMatches.map((item) => [item.handle, item]));
    const matchedBrandStems = directMatches
      .filter((item) => item.handle.startsWith('brand-'))
      .map((item) => item.handle.replace(/^brand-/, ''));

    if (matchedBrandStems.length > 0) {
      collectionIndex.forEach((item) => {
        if (!item.handle?.startsWith('model-')) return;
        const matchedStem = matchedBrandStems.find((stem) => item.handle.startsWith(`model-${stem}-`));
        if (!matchedStem) return;

        const existing = candidatesByHandle.get(item.handle);
        const boostedScore = Math.max(existing?.score || 0, 18);
        candidatesByHandle.set(item.handle, {
          title: item.title,
          handle: item.handle,
          url: item.url,
          score: boostedScore,
        });
      });
    }

    return Array.from(candidatesByHandle.values());
  }

  extractProductCandidatesFromIndex(productIndex, normalizedTerm) {
    if (!Array.isArray(productIndex) || !productIndex.length) return [];

    return productIndex
      .map((item) => ({
        title: item.title,
        handle: item.handle,
        url: item.url,
        imageSrc: item.imageSrc,
        imageAlt: item.imageAlt || item.title,
        score: this.getProductRelevanceScore(item.title, item.handle, normalizedTerm),
      }))
      .filter((item) => item.score > 0);
  }

  extractCollectionCandidates(rootNode, normalizedTerm) {
    if (!rootNode) return [];
    const seenCollections = new Set();
    const collectionCandidates = [];
    const collectionAnchors = rootNode.querySelectorAll(
      'a[href*="/collections/brand-"], a[href*="/collections/model-"], a[href*="/collections/ref-"]'
    );

    Array.from(collectionAnchors).forEach((anchor) => {
      const parsedUrl = this.parseUrl(anchor.getAttribute('href'));
      if (!parsedUrl) return;
      const handle = (parsedUrl.pathname || '').replace('/collections/', '').split('/')[0];
      if (!handle || seenCollections.has(handle)) return;
      if (!handle.startsWith('brand-') && !handle.startsWith('model-') && !handle.startsWith('ref-')) return;

      seenCollections.add(handle);
      const title =
        this.getAnchorTitle(anchor) || handle.replace(/^(brand-|model-|ref-)/, '').replace(/-/g, ' ').trim();
      collectionCandidates.push({
        title,
        handle,
        url: `/collections/${handle}`,
        score: this.getCollectionRelevanceScore(handle, title, normalizedTerm),
      });
    });

    return collectionCandidates;
  }

  extractProductCandidates(rootNode, normalizedTerm) {
    if (!rootNode) return [];
    const seenProducts = new Set();
    const productCandidates = [];
    const productCards = rootNode.querySelectorAll('.card-wrapper.product-card-wrapper');

    Array.from(productCards).forEach((cardWrapper) => {
      const productAnchor = cardWrapper.querySelector(
        'a.full-unstyled-link[href*="/products/"], .card__heading a[href*="/products/"]'
      );
      if (!productAnchor) return;

      const parsedUrl = this.parseUrl(productAnchor.getAttribute('href'));
      if (!parsedUrl) return;
      const handle = (parsedUrl.pathname || '').replace('/products/', '').split('/')[0];
      if (!handle || seenProducts.has(handle)) return;

      const title = this.getAnchorTitle(productAnchor) || handle.replace(/-/g, ' ').trim();
      const score = this.getProductRelevanceScore(title, handle, normalizedTerm);
      if (score <= 0) return;

      const imageElement = cardWrapper.querySelector('.card__media img');
      const imageSrc = this.getImageSource(imageElement);
      const imageAlt = imageElement?.getAttribute('alt') || title;

      seenProducts.add(handle);
      productCandidates.push({
        title,
        handle,
        url: `/products/${handle}`,
        imageSrc,
        imageAlt,
        score,
      });
    });

    return productCandidates;
  }

  rankCollectionCandidates(candidates) {
    return [...candidates].sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.title.localeCompare(b.title, 'ja');
    });
  }

  rankProductCandidates(candidates) {
    return [...candidates].sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.title.localeCompare(b.title, 'ja');
    });
  }

  getCollectionRelevanceScore(handle, title, normalizedTerm) {
    if (!normalizedTerm) return 1;
    const handleNoPrefix = handle.replace(/^(brand-|model-|ref-)/, '');
    const normalizedHandle = this.normalizeForMatch(handleNoPrefix);
    const compactHandle = normalizedHandle.replace(/[-\s]/g, '');
    const compactTerm = normalizedTerm.replace(/[-\s]/g, '');
    const normalizedTitle = this.normalizeForMatch(title);

    if (normalizedHandle === normalizedTerm || compactHandle === compactTerm) return 40;
    if (normalizedHandle.startsWith(normalizedTerm) || compactHandle.startsWith(compactTerm)) return 30;
    if (normalizedHandle.includes(normalizedTerm) || compactHandle.includes(compactTerm)) return 20;
    if (normalizedTitle.includes(normalizedTerm) || normalizedTitle.replace(/[-\s]/g, '').includes(compactTerm)) return 10;
    return 1;
  }

  getProductRelevanceScore(title, handle, normalizedTerm) {
    if (!normalizedTerm) return 1;

    const normalizedTitle = this.normalizeForMatch(title);
    const normalizedHandle = this.normalizeForMatch(handle);
    const compactTitle = normalizedTitle.replace(/[-\s]/g, '');
    const compactHandle = normalizedHandle.replace(/[-\s]/g, '');
    const compactTerm = normalizedTerm.replace(/[-\s]/g, '');

    let score = 0;
    if (normalizedTitle.startsWith(normalizedTerm)) score += 50;
    if (normalizedHandle.startsWith(normalizedTerm)) score += 40;
    if (normalizedTitle.includes(normalizedTerm)) score += 30;
    if (normalizedHandle.includes(normalizedTerm)) score += 20;
    if (compactTitle.includes(compactTerm)) score += 15;
    if (compactHandle.includes(compactTerm)) score += 10;
    return score;
  }

  getImageSource(imageElement) {
    if (!imageElement) return '';
    const src = imageElement.getAttribute('src');
    if (src && src.trim()) return src.trim();

    const srcset = imageElement.getAttribute('srcset');
    if (!srcset || !srcset.trim()) return '';
    const firstCandidate = srcset.split(',')[0]?.trim();
    if (!firstCandidate) return '';
    return firstCandidate.split(' ')[0] || '';
  }

  parseUrl(hrefValue) {
    if (!hrefValue) return null;
    try {
      return new URL(hrefValue, window.location.origin);
    } catch (_error) {
      return null;
    }
  }

  normalizeImageUrl(src) {
    if (!src) return '';
    const trimmed = src.trim();
    if (trimmed.startsWith('//')) return `https:${trimmed}`;
    return trimmed;
  }

  getAnchorTitle(anchor) {
    const labelledText = anchor.getAttribute('aria-label');
    if (labelledText && labelledText.trim()) return labelledText.trim();

    const directText = anchor.textContent;
    if (directText && directText.trim()) return directText.trim();

    const cardWrapper = anchor.closest('.card-wrapper');
    const cardTitle = cardWrapper?.querySelector('.card-information .full-unstyled-link, .card__heading a, .h5');
    if (cardTitle?.textContent && cardTitle.textContent.trim()) return cardTitle.textContent.trim();

    return '';
  }

  normalizeForMatch(text) {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .normalize('NFKC')
      .replace(/[_/]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  escapeHtml(text) {
    return (text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.renderLoadingPlaceholder();
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
    this.open();
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.input.setAttribute('aria-activedescendant', '');
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    this.marketSearch?.render();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.removeLoadingPlaceholder();
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeLoadingPlaceholder();
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  renderLoadingPlaceholder() {
    if (this.querySelector('#predictive-search-results-groups-wrapper')) return;
    if (this.querySelector('[data-predictive-loading-placeholder]')) return;

    const loadingPlaceholder = document.createElement('div');
    loadingPlaceholder.className = 'predictive-search__loading-placeholder';
    loadingPlaceholder.setAttribute('data-predictive-loading-placeholder', 'true');
    loadingPlaceholder.innerHTML = `
      <div class="predictive-search__loading-placeholder-inner">
        <span class="predictive-search__loading-placeholder-text">候補を読み込み中...</span>
      </div>
    `;
    this.predictiveSearchResults.appendChild(loadingPlaceholder);
  }

  removeLoadingPlaceholder() {
    this.querySelector('[data-predictive-loading-placeholder]')?.remove();
  }
}

customElements.define('predictive-search', PredictiveSearch);
